// Keyword Optimiser — the PRD's five sub-features.
//
// One model for every keep, block or add decision, always as a suggestion.
// Nothing reaches Amazon without the seller's approval, everything is logged,
// and every applied change can be undone.
//
// Applying is switched off in this deployment and the page says so plainly
// rather than letting a seller approve into a void.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiArrowLeft, FiLink } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './KeywordOptimiser.module.css';
import AuthContext from '../../../context/AuthContext';
import FeatureCards from './components/FeatureCards';
import Suggestions from './components/Suggestions';
import Discovery from './components/Discovery';
import Changes from './components/Changes';
import Brands from './components/Brands';
import { NotConnectedError, applyOne, approveMany, decide, discover, getBrands,
         getChanges, getOverview, markIntentional, saveBrands,
         undoOne } from './api';

const TABS = [
  { id: 'list', label: 'What to change', ref: '' },
  { id: 'negatives', label: 'Block', ref: 'SF1' },
  { id: 'harvest', label: 'Promote', ref: 'SF2' },
  { id: 'conflicts', label: 'Overlaps', ref: 'SF3' },
  { id: 'discovery', label: 'Discover', ref: 'SF4' },
  { id: 'listing', label: 'Listing sync', ref: 'SF5' },
  { id: 'changes', label: 'Change log', ref: '' },
  { id: 'brands', label: 'Your brands', ref: '' },
];

export default function KeywordOptimiser() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [tab, setTab] = useState('list');
  const [overview, setOverview] = useState(null);
  const [plan, setPlan] = useState(null);
  const [changes, setChanges] = useState(null);
  const [brands, setBrands] = useState(null);
  const [loading, setLoading] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [notConnected, setNotConnected] = useState(null);
  const [reload, setReload] = useState(0);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(e.message);
    else setError(e.message);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let live = true;
    setLoading(true);
    getOverview(token, {})
      .then((r) => { if (live) { setOverview(r); setNotConnected(null); setError(null); } })
      .catch((e) => { if (live) fail(e); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [token, fail, reload]);

  useEffect(() => {
    if (!token || tab !== 'changes') return;
    getChanges(token, {}).then((r) => setChanges(r.changes)).catch(fail);
  }, [token, tab, fail, reload]);

  useEffect(() => {
    if (!token || tab !== 'brands') return;
    getBrands(token).then(setBrands).catch(fail);
  }, [token, tab, fail, reload]);

  const onDecide = async (suggestion, decision) => {
    setBusy(true); setNotice(null);
    try {
      const r = await decide(token, suggestion.id, decision);
      setNotice(decision === 'approve'
        ? (r.apply_note || 'Approved.')
        : decision === 'dismiss' ? 'Set aside.' : 'Back on the list.');
      setReload((n) => n + 1);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onApproveMany = async (ids) => {
    setBusy(true); setNotice(null);
    try {
      const r = await approveMany(token, ids);
      setNotice(`${r.total} approved. ${r.apply_note || ''}`.trim());
      setReload((n) => n + 1);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onApply = async (suggestion) => {
    setBusy(true); setNotice(null);
    try {
      const r = await applyOne(token, suggestion.id, true);
      const what = (r.steps || []).map((step) =>
        `${step.method || 'listing'} ${step.path || ''} — ${step.what}`).join(' · ');
      setNotice(`${r.reason} ${what}`.trim());
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onUndo = async (idOrSuggestion) => {
    const id = typeof idOrSuggestion === 'string' ? idOrSuggestion : idOrSuggestion.id;
    setBusy(true); setNotice(null);
    try {
      const r = await undoOne(token, id, true);
      setNotice(r.reason);
      setReload((n) => n + 1);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onIntentional = async (pair) => {
    setBusy(true);
    try {
      await markIntentional(token, pair, false);
      setNotice('Marked deliberate. It will not be raised again.');
      setReload((n) => n + 1);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onDiscover = async (asins) => {
    setPlanning(true); setError(null); setNotice(null);
    try {
      setPlan(await discover(token, asins));
    } catch (e) { fail(e); } finally { setPlanning(false); }
  };

  const onSaveBrands = async (body) => {
    setBusy(true);
    try { setBrands(await saveBrands(token, body)); setReload((n) => n + 1); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };

  const listFor = (feature) => (!overview ? null : {
    ...overview,
    suggestions: overview.suggestions.filter((x) => x.feature === feature),
  });

  function body() {
    if (notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Amazon Ads is not connected</h2>
          <p className={s.lockedBody}>
            {notConnected} Advertising is a second Amazon authorisation, separate from
            the Selling Partner one — connect it on the Amazon card and collect the
            last sixty days. Discovery can still work from Search Query Performance
            once Brand Registry data is in.
          </p>
          <button className={s.button} onClick={() => navigate('/dashboard/market-places')}>
            Go to the Amazon card
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}
        {notice && <div className={s.notice}>{notice}</div>}

        {overview && (
          <section className={s.panel}>
            <div className={s.contextBar}>
              <div className={s.coverage}>
                {overview.window?.excluded_note}
                {' '}Target ACOS {(overview.target_acos * 100).toFixed(0)}%
                {overview.target_source === 'benchmark'
                  ? ' (a benchmark, not a target you set)' : ''}.
                {' '}Decisions are made at {overview.confidence_level}% confidence.
              </div>
            </div>
            {overview.freshness?.stale && (
              <div className={s.coverageWarn}>
                <FiAlertTriangle /> {overview.freshness.why}
              </div>
            )}
          </section>
        )}

        <FeatureCards data={overview} loading={loading && !overview}
                      onOpen={(next) => setTab(next === 'discovery' ? 'discovery' : next)} />

        <section className={s.panel}>
          <div className={s.tabs}>
            {TABS.map((t) => (
              <button key={t.id} className={tab === t.id ? s.tabActive : s.tab}
                      onClick={() => setTab(t.id)}>
                {t.label}{t.ref && <span className={s.tabRef}>{t.ref}</span>}
              </button>
            ))}
          </div>

          <div style={{ paddingTop: 16 }}>
            {tab === 'list' && (
              <Suggestions data={overview} currency={overview?.currency}
                           onDecide={onDecide} onApply={onApply} onUndo={onUndo}
                           onApproveMany={onApproveMany} onIntentional={onIntentional}
                           busy={busy} />
            )}
            {['negatives', 'harvest', 'conflicts', 'listing'].includes(tab) && (
              <Suggestions data={listFor(tab)} currency={overview?.currency}
                           onDecide={onDecide} onApply={onApply} onUndo={onUndo}
                           onApproveMany={onApproveMany} onIntentional={onIntentional}
                           busy={busy} />
            )}
            {tab === 'discovery' && (
              <Discovery plan={plan} loading={planning} onDiscover={onDiscover}
                         busy={busy} />
            )}
            {tab === 'changes' && (
              <Changes changes={changes} loading={changes === null}
                       onUndo={onUndo} busy={busy} />
            )}
            {tab === 'brands' && (
              <Brands brands={brands} onSave={onSaveBrands} busy={busy} />
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Keyword Optimiser</h1></div>
          <p>Block what cannot pay back, promote what already sells, and find what you
             are missing</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
